import { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';

const DEFAULT_TERMINOLOGY = {
  employer_role: "Employer",
  admin_role: "Principal Agent",
  supervisor_role: "Principal Agent",
  contractor_role: "Contractor",
  site_access: "Possession of the Site",
  completion_primary: "Practical Completion",
  completion_sectional: "Sectional Completion",
  completion_final: "Final Completion",
  programme_baseline: "Contract Programme",
  programme_tender: "Tender Programme",
  contract_data_name: "Contract Data",
  special_conditions: "Special Conditions / Amendments",
  delay_event: "Delay Event",
  notice: "Notice",
  claim: "Claim",
  determination: "Principal Agent Determination",
  start_date_label: "Date of Possession of Site",
  end_date_label: "Practical Completion Date",
};

const terminologyCache = {};

export function useTerminology(projectId) {
  const [terminology, setTerminology] = useState(DEFAULT_TERMINOLOGY);
  const [contractFamily, setContractFamily] = useState("JBCC");
  const [loading, setLoading] = useState(false);

  const loadTerminology = useCallback(async () => {
    if (!projectId) return;

    if (terminologyCache[projectId]) {
      setTerminology(terminologyCache[projectId].terminology);
      setContractFamily(terminologyCache[projectId].contractFamily);
      return;
    }

    setLoading(true);
    try {
      const res = await api.documentControl.getProjectTerminology(projectId);
      const t = res.data.terminology || DEFAULT_TERMINOLOGY;
      const cf = res.data.contract_family || "JBCC";
      terminologyCache[projectId] = { terminology: t, contractFamily: cf };
      setTerminology(t);
      setContractFamily(cf);
    } catch {
      // Fallback to default JBCC terminology
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadTerminology();
  }, [loadTerminology]);

  const isNEC = contractFamily?.startsWith('NEC');
  const isFIDIC = contractFamily === 'FIDIC';

  const t = (key) => terminology[key] || DEFAULT_TERMINOLOGY[key] || key;

  return {
    terminology,
    contractFamily,
    loading,
    isNEC,
    isFIDIC,
    t,
    eventsLabel: t('delay_event') + 's',
    noticesLabel: t('notice') + 's',
    claimsLabel: t('claim') + 's',
    eventLabel: t('delay_event'),
    noticeLabel: t('notice'),
    claimLabel: t('claim'),
    adminRole: t('admin_role'),
    determinationLabel: t('determination'),
  };
}
